import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { api, ErroRede } from './api.js'
import {
  guardarSessao, lerSessao, esquecerUsuario, iniciarSincronizacaoAutomatica,
} from './offline.js'

const ContextoSessao = createContext(null)

export function ProvedorSessao({ children }) {
  const [usuario, setUsuario] = useState(null)
  const [carregando, setCarregando] = useState(true)

  const recarregar = useCallback(async () => {
    try {
      const { usuario: u } = await api.get('/auth/eu')
      setUsuario(u)
      return u
    } catch (erro) {
      // Sem rede não é sessão inválida: segue com a última que o servidor confirmou,
      // para o formulário offline abrir. Quem decide de verdade é o servidor, quando
      // a conexão voltar — e o `online` abaixo pergunta de novo.
      if (erro instanceof ErroRede) {
        const guardada = await lerSessao().catch(() => null)
        setUsuario(guardada ? { ...guardada, offline: true } : null)
        return guardada ?? null
      }
      setUsuario(null)
      return null
    } finally {
      setCarregando(false)
    }
  }, [])

  useEffect(() => { recarregar() }, [recarregar])

  // Toda sessão confirmada pelo servidor vira a "última" — login, recarga ou troca
  // de senha, num lugar só. A que veio do cache não se regrava.
  useEffect(() => {
    if (usuario && !usuario.offline) guardarSessao(usuario).catch(() => {})
  }, [usuario])

  // Sessão vinda do cache precisa ser confirmada assim que houver rede.
  useEffect(() => {
    if (!usuario?.offline) return undefined
    window.addEventListener('online', recarregar)
    return () => window.removeEventListener('online', recarregar)
  }, [usuario?.offline, recarregar])

  // A fila é por usuário: a sincronização começa quando se sabe quem está logado.
  // Se o servidor disser que a sessão caiu (senha trocada em outro aparelho, prazo
  // vencido), pergunta de novo — o 401 leva ao login, e a fila espera intacta.
  const usuarioId = usuario?.id ?? null
  useEffect(() => {
    if (!usuarioId) return undefined
    return iniciarSincronizacaoAutomatica(usuarioId, { aoPerderSessao: recarregar })
  }, [usuarioId, recarregar])

  const entrar = useCallback(async (email, senha) => {
    const { usuario: u } = await api.post('/auth/login', { email, senha })
    setUsuario(u)
    return u
  }, [])

  // Os lançamentos ainda na fila ficam no aparelho, guardados com o dono: são
  // enviados quando ele entrar de novo, e nunca pela conta de outra pessoa.
  const sair = useCallback(async () => {
    await api.post('/auth/logout').catch(() => {})
    if (usuario) await esquecerUsuario(usuario.id).catch(() => {})
    setUsuario(null)
  }, [usuario])

  return (
    <ContextoSessao.Provider value={{ usuario, carregando, entrar, sair, recarregar, setUsuario }}>
      {children}
    </ContextoSessao.Provider>
  )
}

export const useSessao = () => useContext(ContextoSessao)
